import {useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';
import {SearchX} from 'lucide-react';
import {
  Badge,
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useJob,
  useJobItems,
  useClassificationConfig,
  useLanguages,
  useTopics,
  useLocations
} from '../api/hooks';
import {ItemsGrid} from '../components/items/ItemsGrid';
import {ClassifyDialog} from '../components/investigations/ClassifyDialog';
import {type Item} from '../api/items';
import {useSetItemFlag} from '@/components/items/useSetItemFlag';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {jobStatusLabel, jobStatusTone, toneBadge} from '@/lib/status';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';

export function JobDetailPage() {
  const {id = ''} = useParams();
  const jobQuery = useJob(id);
  const jobItemsQuery = useJobItems(id, {page: 1});
  const items = jobItemsQuery.data?.results ?? [];
  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  const setItemFlag = useSetItemFlag();
  // Stable, so the grid's memoised cards don't all re-render with each render of this page.
  const openClassify = useCallback((item: Item) => {
    setClassifyItems([item]);
  }, []);

  // A job's item list and classify action, independent of Investigations' own grid (which filters
  // to Source: "investigation" jobs and has its own triage/grouping) - reachable from anywhere a
  // job id is known, e.g. a Queues escalation link.
  const {data: classifications = []} = useClassificationConfig();
  const {data: languages = []} = useLanguages();
  const {data: topics = []} = useTopics();
  const {data: locations = []} = useLocations();

  if (jobQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-4">
        <Spinner />
        <span className="text-muted-foreground text-xs">Loading job…</span>
      </div>
    );
  }

  const job = jobQuery.data;
  if (!job) {
    return (
      <div className="p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchX />
            </EmptyMedia>
            <EmptyTitle>Job not found</EmptyTitle>
            <EmptyDescription>No job exists with this id.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <PageHeaderActions>
        <SensitiveMediaToggle />
      </PageHeaderActions>
      <Card size="sm">
        <CardHeader>
          <CardTitle className="truncate">{job.search_term}</CardTitle>
          <CardDescription>
            {job.strategy} · {job.affiliate} · {job.item_count} items
          </CardDescription>
          <CardAction>
            <Badge {...toneBadge(jobStatusTone(job.status))}>{jobStatusLabel(job.status)}</Badge>
          </CardAction>
        </CardHeader>
      </Card>
      <ItemsGrid items={items} onSelect={openClassify} onSetFlag={setItemFlag} />

      <ClassifyDialog
        items={classifyItems}
        classifications={classifications}
        languages={languages}
        topics={topics}
        locations={locations}
        origin="investigations"
        onClose={() => {
          setClassifyItems([]);
        }}
        onSubmitted={() => {
          void jobItemsQuery.refetch();
        }}
      />
    </div>
  );
}
